import { defineComponent as _defineComponent } from 'vue'
import SimilarTitles from '@/components/SimilarTitles.vue';
import BuyBoxBanner from '@/features/buybox/components/BuyBoxBanner.vue';
import ListBuyBox from '@/features/buybox/components/list/ListBuyBox.vue';
import { TitleOfferFragment } from '@/features/buybox/graphql/fragments/Offer.fragment';
import { streamingChartCountries } from '@/components/details/streaming-chart/streaming-chart-config';
import { useI18n } from '@/helpers/composables/useI18n';
import type { AssertByTypename } from '@/helpers/graphql-model-helper';
import { type SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import { TitleDetail } from '@/interfaces/title-details-graphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import { GetNodeTitleDetailsQuery } from '@/pages/graphql/queries/GetNodeTitleDetails.query';
import SectionContainer from '@/pages/titleDetailV1/components/SectionContainer.vue';
import TitleDetailJumpLinksV1 from '@/pages/titleDetailV1/v1/TitleDetailJumpLinksV1.vue';
import TitleDetailTitleClipsV1 from '@/pages/titleDetailV1/v1/TitleDetailTitleClipsV1.vue';
import {
	isTitleSeason,
	isTitleShow,
	useTitleDetailInfo,
} from '@/pages/titleDetailV1/v1/composables/useTitleDetailInfo';
import TitleDetailHeroV1 from '@/pages/titleDetailV1/v1/hero/TitleDetailHeroV1.vue';
import TitleDetailTitleSidebarV1 from '@/pages/titleDetailV1/v1/sidebar/TitleDetailTitleSidebarV1.vue';
import { NewBuyBoxView } from '@/stores/useUserStoreTypes';
import { SNIGEL_EXP_COUNTRIES } from '@/helpers/ad/ad-helper';
import { TrafficHelper } from '@/helpers/traffic-helper';

import { computed, defineAsyncComponent, provide, ref } from 'vue';
// EXP_PROVIDER_BUYBOX_V2
import { provideLocal, useMounted } from '@vueuse/core';
import { PROVIDER_EXP_BB_PROVIDER_FILTER_KEY } from '@/components/experiments/ProviderBuyboxExp/composables/useProviderBuyboxFilter';
// EXP_PROVIDER_BUYBOX_V2
// EXP_SNIGEL
import { SNIGEL_AUCTION_TITLE_KEY } from '@/helpers/ad/snigel-helper';
// EXP_SNIGEL

import BuyBoxTypeSwitcher from '@/features/buybox/components/BuyBoxTypeSwitcher.vue';
import GridBuyBox from '@/features/buybox/components/grid/GridBuyBox.vue';
import PromotedGridBuyBox from '@/features/buybox/components/grid/PromotedGridBuyBox.vue';
import { TextRecommendationModalV2 } from '@/lazy-load-components';
import TextRecommendations from '@/features/why-to-watch/components/TextRecommendations.vue';
import SeasonListV1 from '@/pages/titleDetailV1/v1/SeasonListV1.vue';

import { ModuleContentStreamingCharts, MonetizationType, ObjectType, ProfileType } from '@/@types/graphql-types';
import { disableScroll, enableScroll } from '@/helpers/scroll-helper';
import { extractBuyboxOffers, getTVODBackdropClips } from '@/helpers/title-helper';

import { useInternationalOffersStatus } from '@/features/international-offers/composables/useInternationalOffers';
import { useWtwTracking } from '@/features/why-to-watch/tracking';
import StreamingOffersHistory from '@/features/streaming-offers-history/components/StreamingOffersHistory.vue';
import { storeToRefs } from 'pinia';
import { useLanguageStore } from '@/stores/pinia/useLanguageStore';
import { useRoutingStore } from '@/stores/pinia/useRoutingStore';
import { useBuyBoxData } from '@/features/buybox/composables/useBuyboxData';
import { useGetAdServerPlacement } from '@/features/ad-server/composables/useAdServer';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { useActivateExperiment, useExperiment } from '@/libraries/experiment';
import { useBuyboxView } from '@/features/buybox/composables/useBuyboxView';
import { useArbitrage } from '@/helpers/composables/useArbitrage';
import { usePromotion } from '@/helpers/composables/usePromotion';
import { useSponsoredRec } from '@/features/sponsored-recommendations/composables/useSendBidId';
import { isJWTVRentalOffer } from '@/features/jwtv-titles-rental/helpers/jwtv-titles-rental-helper';


export default /*#__PURE__*/_defineComponent({
  __name: 'TitleDetailV1',
  props: {
    titleDetailNode: null,
    promotedOffers: { default: () => [] },
    metaData: null,
    genresByShortName: null,
    hidePromotedBuyBox: { type: Boolean },
    _sponsoredAd: { default: undefined },
    entityId: null,
    isLoading: { type: Boolean },
    isPerceivedLoading: { type: Boolean },
    additionalContexts: { default: () => [] }
  },
  emits: ['buyboxLoading', 'closeTitleDetailModal'],
  setup(__props: any, { emit: emits }) {

const props = __props as { titleDetailNode: AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Movie' | 'Show' | 'Season'> | undefined, promotedOffers: TitleOfferFragment[], metaData: any, genresByShortName: any, hidePromotedBuyBox: boolean, _sponsoredAd: SponsoredAdFragment | undefined, entityId?: string | undefined, isLoading?: boolean, isPerceivedLoading: boolean, additionalContexts: SnowplowContext[] };

const AVODHeroSection = defineAsyncComponent(() => import('@/pages/titleDetailV1/v1/hero/AVODHeroSection.vue'));
const TVODHeroSection = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/hero/TVODHero/TVODHeroSection.vue')
);

const TitleDetailStreamingChart = defineAsyncComponent(
	() => import('@/components/details/streaming-chart/TitleDetailStreamingChart.vue')
);
const TitleSynopsis = defineAsyncComponent(() => import('@/components/details/TitleSynopsis.vue'));

const ArticleBlock = defineAsyncComponent(() => import('@/components/details/editorial/ArticleBlock.vue'));
const UpcomingContent = defineAsyncComponent(() => import('@/components/upcoming/UpcomingContent.vue'));
const EpisodesList = defineAsyncComponent(() => import('@/components/EpisodesList.vue'));

const TitleCredits = defineAsyncComponent(() => import('@/components/details/TitleCredits.vue'));
const SpinningText = defineAsyncComponent(() => import('@/components/details/SpinningText.vue'));
const TitleDetailSimilarTitlesV2 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/TitleDetailSimilarTitlesV2.vue')
);
const TitleDetailQuickActionBarV1 = defineAsyncComponent(
	() => import('@/pages/titleDetailV1/v1/sidebar/TitleDetailQuickActionBarV1.vue')
);
const AdUnit = defineAsyncComponent(() => import('@/components/AdUnit.vue'));
// EXP_SNIGEL
const SnigelExpAdUnit = defineAsyncComponent(() => import('@/components/experiments/SnigelExpAdUnit.vue'));
// EXP_SNIGEL

// need to import these immediately because it's above the fold and could cause CLS
const activeClipId = ref<string | null>(null);
const streamingChart = ref<{ renderChart?: boolean } | null>(null);
const container = ref(null);
const displayTextRecommendationModal = ref<boolean>(false);

const userStore = useUserStore();
const { newBuyboxView, buyboxExpView, isAdmin, isEditor, loggedInNewBuyboxView, isLoggedIn, experimentsLoaded } =
	storeToRefs(userStore);

const { country, language } = storeToRefs(useLanguageStore());
const { urlMetadata } = storeToRefs(useRoutingStore());
const { t } = useI18n();




const {
	title,
	titleName,
	titleId,
	titleObjectId,
	seasonName,
	seasonNumber,
	seasonTotalEpisodeCount,
	isShow,
	isSeason,
	recentEpisodes,
	recentEpisodesAvailable,
	isTitleUpcoming,
	showDetails,
	originalTitle,
	isInWatchList,
} = useTitleDetailInfo(() => props.titleDetailNode);

const {
	isActive: isInternationalOffersActive,
	hasInternationalOffers,
	isInternationalOffersTitle,
} = useInternationalOffersStatus({
	title: () => props.titleDetailNode,
});

const { AVODOffer, upcomingReleases } = useBuyBoxData({
	title: () => props.titleDetailNode as TitleDetail,
	buyboxUpcomingReleases: () => props.titleDetailNode?.content?.upcomingReleases ?? [],
});

/* Ad Server - fetches buybox ad server placement once for both List & Grid buybox */
useGetAdServerPlacement({
	title: () => props.titleDetailNode as TitleDetail,
	enabled: () => !!props.titleDetailNode,
});

const { isArbitrating } = useArbitrage();

// Grid Swiper Exp
const activateExpGridSwiper = computed(
	() =>
		!isArbitrating.value &&
		!!props.titleDetailNode &&
		!props.titleDetailNode.offers.some(offer => ['amp', 'hlu'].includes(offer.package.shortName))
	// exclude Prime and Hulu titles, because these are not compatible with the Prime SQTV / Hulu QTV placements
);
useActivateExperiment('EXP_GRID_SWIPER', activateExpGridSwiper);

const { catalogueItem } = usePromotion({
	offers: () => props.titleDetailNode?.offers ?? [],
	title: () => props.titleDetailNode,
});

const activateGridRedesignExps = computed(
	() =>
		!isArbitrating.value &&
		!isInternationalOffersActive.value &&
		!!props.titleDetailNode &&
		props.titleDetailNode.objectType === ObjectType.Movie
);

const { activeVariant: gridMoreInfoActiveVariant } = useActivateExperiment(
	'EXP_GRID_MORE_INFO_REDESIGN_V2',
	activateGridRedesignExps
);

// EXP MERGE BUY RENT
const activateMBRExp = computed(() => {
	return !isInternationalOffersActive.value && upcomingReleases.value.length === 0 && !isArbitrating.value;
});
const { activeVariantWithControlGroup: mergeBuyRentExpVariant } = useActivateExperiment(
	'EXP_MERGE_BUY_RENT',
	activateMBRExp
);
// EXP MERGE BUY RENT

// Underpreforming urls exp 5

const activateUnderperformingUrlsExp = computed(
	() =>
		props.metaData.inLists.includes('EXP_LOWPERF_45') &&
		!props.titleDetailNode?.offers.some(offer => offer.package.shortName === 'amp') &&
		props.titleDetailNode?.objectType === ObjectType.Movie
);

useActivateExperiment('EXP_UNDERPERFORMING_URLS_6', activateUnderperformingUrlsExp);

// Important - declare Grid bbox experiments before useBuyboxView, as experiments affect the return value of useBuyboxView

// OLD BUYBOX EXP
const { isGridBuyboxDefault } = useBuyboxView({ objectType: () => props.titleDetailNode?.objectType });
// OLD BUYBOX EXP

const activatePrimePlayPromoExp = computed(() => {
	return (
		!!props.titleDetailNode &&
		(props.titleDetailNode.offers ?? []).some(offer => offer.package.shortName === 'amp') &&
		catalogueItem.value?.shortName === 'amp'
	);
});

useActivateExperiment('EXP_PRIME_PLAY_PROMO', activatePrimePlayPromoExp);

// EXP_SNIGEL
const isSnigelAdSpaceAllowed = computed(
	() => !TrafficHelper.isCampaignTraffic() && !urlMetadata.value?.htmlContentJson?.exclude_from_ads
);
const { activeVariantWithControlGroup: snigelExpVariant, experimentInfo: snigelExpInfo } = useActivateExperiment(
	'EXP_SNIGEL',
	isSnigelAdSpaceAllowed
);

// shared across every SnigelExpAdUnit instance on this page so startSnigelAuction() runs once per
// title instead of once per ad unit - see snigel-helper.ts
provideLocal(SNIGEL_AUCTION_TITLE_KEY, ref<string | null>(null));
// EXP_SNIGEL

// only countries that are enabled for international offers get the noOffer prop filled
provide('noOffer', isInternationalOffersActive.value);

// EXP_PROVIDER_BUYBOX_V2: shared ref so provider filter persists when switching grid ↔ list
const sharedBuyboxProviderFilter = ref<string[] | null>(null);
provideLocal(PROVIDER_EXP_BB_PROVIDER_FILTER_KEY, sharedBuyboxProviderFilter);
// EXP_PROVIDER_BUYBOX_V2

const buyboxOffers = computed(() => {
	if (!props.titleDetailNode) {
		return null;
	}
	return extractBuyboxOffers(props.titleDetailNode);
});

const { isListSponsoredAd, isShadowCampaign, hasActiveSRCampaign } = useSponsoredRec({
	sponsoredAd: () => props._sponsoredAd ?? null,
	objectType: () => props.titleDetailNode?.objectType ?? null,
});

const streamingCharts = computed(() => {
	const streamingChartsModule = props.titleDetailNode?.titleModules.find(
		module => module?.template.technicalName === 'TITLE_STREAMING_CHARTS'
	);
	return (streamingChartsModule?.content as ModuleContentStreamingCharts)?.Titles ?? [];
});

const hasNonCinemaOffers = computed(
	() => !!props.titleDetailNode?.offers?.some(offer => offer.monetizationType !== MonetizationType.Cinema)
);

// here we only check if the current title has any streaming charts informations.
// it won't have informations about titles up or below the title's streaming charts' rank.
const displayStreamingChart = computed(() => {
	if (!streamingChartCountries.includes(country.value) || !props.titleDetailNode?.streamingCharts) {
		return false;
	}

	// temporary hack for broken ES logic
	if (!hasNonCinemaOffers.value) return false;

	const rank = props.titleDetailNode?.streamingCharts.edges?.[0]?.streamingChartInfo?.rank;
	return !!rank && rank < 10000;
});

const hasJWTVRentalOffer = computed(() => {
	const jwtvOffer = props.titleDetailNode?.offers.find(offer => offer.package.shortName === 'jwt');
	return jwtvOffer && isJWTVRentalOffer(jwtvOffer);
});
const activeHeroSection = computed((): 'TVOD' | 'AVOD' | 'Default' => {
	const hasJWTVAVOD = props.titleDetailNode?.offers.some(
		offer => offer.package.shortName === 'jwt' && offer.monetizationType === MonetizationType.Free
	);
	if (hasJWTVAVOD) {
		return 'AVOD';
	} else if (
		props.titleDetailNode &&
		['AU', 'NZ'].includes(country.value) &&
		!isInternationalOffersActive.value &&
		!!hasJWTVRentalOffer.value &&
		getTVODBackdropClips(props.titleDetailNode).length > 0
	) {
		return 'TVOD';
	}
	return 'Default';
});

const overrideDefaultBuyBoxType = ref<NewBuyBoxView>(
	activeHeroSection.value === 'AVOD' ? NewBuyBoxView.LIST : NewBuyBoxView.GRID
);

const overrideBuyBoxTypeActive = computed(() => activeHeroSection.value === 'AVOD' && !isLoggedIn.value);

const buyBoxTypeBindings = computed(() =>
	overrideBuyBoxTypeActive.value ? { buyBoxType: overrideDefaultBuyBoxType.value } : {}
);

function updateOverrideBuyBoxType(view: { buyBoxType: NewBuyBoxView }) {
	if (!overrideBuyBoxTypeActive.value) return;
	overrideDefaultBuyBoxType.value = view.buyBoxType;
}

const showSynopsis = computed(
	() =>
		!(
			props.metaData?.htmlContentJson?.detail_heading != null &&
			props.metaData?.htmlContentJson?.detail_content != null
		) && !!props.titleDetailNode?.content?.shortDescription
);

const editorialSynopsis = computed(() => props.titleDetailNode?.content?.editorialContent?.synopsis ?? null);

const showSimilarTitles = computed(() => !!props.titleDetailNode && !props.metaData?.htmlContent.hide_similar_titles);

const aboutHeadline = computed(() => {
	return isSeason.value
		? t('WEBAPP_ABOUT_THE_SEASON')
		: isShow.value
		? t('WEBAPP_ABOUT_THE_TV_SHOW')
		: t('WEBAPP_ABOUT_THE_MOVIE');
});

const showNewBuyBox = computed(() => {
	if (!!overrideBuyBoxTypeActive.value) {
		return overrideDefaultBuyBoxType.value !== NewBuyBoxView.GRID;
	}

	// MERGE BUY RENT EXP
	if (!!mergeBuyRentExpVariant.value) return true;
	// MERGE BUY RENT EXP

	if (isInternationalOffersTitle.value) {
		return true;
	}

	// OLD BUYBOX EXP
	if (isGridBuyboxDefault.value) {
		return buyboxExpView.value !== NewBuyBoxView.GRID;
	}
	// OLD BUYBOX EXP

	if (isLoggedIn.value && (isSeason.value || isShow.value)) {
		return loggedInNewBuyboxView.value !== NewBuyBoxView.GRID;
	}

	return newBuyboxView.value !== NewBuyBoxView.GRID;
});

const showOffersHistory = computed(
	() =>
		!isLoggedIn.value &&
		!!props.titleDetailNode?.offersHistory?.length &&
		props.titleDetailNode.offersHistory.length > 0 &&
		!hasInternationalOffers.value
);

const episodesTitle = computed(() => {
	// only show this for now for show page release - tbc
	if (isSeason.value) return t('WEBAPP_X_EPISODES', { episodes: seasonTotalEpisodeCount.value || '' });
	return t('WEBAPP_WATCH_NEWEST_EPISODES');
	// return recentEpisodesAvailable.value ? t('WEBAPP_RECENT_EPISODES') : t('WEBAPP_WATCH_NEWEST_EPISODES');
});

const maxEpisodes = computed(() =>
	recentEpisodesAvailable.value ? recentEpisodes.value.length : seasonTotalEpisodeCount.value
);

const episodes = computed(() =>
	recentEpisodesAvailable.value
		? recentEpisodes.value
		: (props.titleDetailNode as AssertByTypename<GetNodeTitleDetailsQuery['node'], 'Season'>).episodes || []
);

const showTextRecommendationTools = computed(() => isAdmin.value || isEditor.value);

/* Ads */

// we don't want to display too many ads in one viewport.
// that's why we're taking out the 3rd ad slot, if it's:
// - on a page without streaming charts (that would push down the content vertically)
// - only in DE
const subgenres = computed(() => props.titleDetailNode?.content.subgenres ?? []);
const isMounted = useMounted();

const isExperimentsLoadedAndNonSnigel = computed(
	() =>
		(isMounted.value && experimentsLoaded.value && !snigelExpInfo.value.variant) ||
		!SNIGEL_EXP_COUNTRIES.includes(country.value)
);

const displayThirdAd = computed(() => {
	return !['DE'].includes(country.value) || displayStreamingChart.value;
});

/* Clips */

const titleClips = computed(() => {
	// check for localised trailers, if not use fallbacks
	const localisedClips = getTitleClips(props.titleDetailNode, 'content');
	if (localisedClips.length) return localisedClips;

	return getTitleClips(props.titleDetailNode, 'fallBackClips');
});

const hasTrailers = computed(() => titleClips.value.length > 0);

function getTitleClips(titleNode: typeof props.titleDetailNode, key: 'content' | 'fallBackClips') {
	const youtubeClips = titleNode?.[key]?.clips ?? [];
	if (youtubeClips.length) return youtubeClips;

	const dailymotionClips = titleNode?.[key]?.dailymotionClips ?? [];
	return dailymotionClips.length ? [dailymotionClips[0]] : [];
}

const { trackWtw } = useWtwTracking({ title: () => props.titleDetailNode });

const promotedBundles = computed(() => props.titleDetailNode?.promotedBundles ?? []);
const hasPromotedOffers = computed(() => props.promotedOffers.length + promotedBundles.value.length > 0);

function openTextRecommendationModal() {
	// if (preventNonSignedInAction(AccountModes.REGISTRATION_OPTIONS)) return;

	trackWtw('modalOpened');

	displayTextRecommendationModal.value = true;
	disableScroll();
}

function closeTextRecommendationModal() {
	displayTextRecommendationModal.value = false;
	enableScroll();
}

const textRecommendationArray = computed(() => {
	const textRecs = props.titleDetailNode?.content?.textRecommendations ?? [];

	if (isLoggedIn.value) return textRecs;

	// hide user text recommendations for non-logged-in users, for SEO
	return textRecs.filter(textRec => ![ProfileType.User, ProfileType.ProUser].includes(textRec.profile.profileType));
});

const visibleSectionState = computed(() => {
	return {
		trailers: !!(hasTrailers.value && props.titleDetailNode),
		'why-to-watch': textRecommendationArray.value?.length > 0 && isLoggedIn.value,
		synopsis: showSynopsis.value || editorialSynopsis != null,
		'streaming-chart': displayStreamingChart.value && !!streamingChart.value?.renderChart,
		'episodes-lists': !isTitleUpcoming.value && (isSeason.value || isShow.value),
		'regular-buybox': !(isTitleUpcoming.value && !showNewBuyBox.value),
		'similar-content': showSimilarTitles.value,
	};
});

// EXP_PROVIDER_BUYBOX_V2
const { experimentInfo: startPageJwtvInfo } = useExperiment('EXP_START_PAGE_JWTV');
const wasAcquiredToStartPageJwtv = computed(() => {
	return !!startPageJwtvInfo.value.activated;
});
const activateProviderBuyboxExp = computed(
	() => !isInternationalOffersActive.value && !wasAcquiredToStartPageJwtv.value
);
useActivateExperiment('EXP_PROVIDER_BUYBOX_V2', activateProviderBuyboxExp);
// EXP_PROVIDER_BUYBOX_V2

// EXP_LINEAR_TV
const activateLinearTVExp = computed(() =>
	buyboxOffers.value?.linear && buyboxOffers.value?.linear.length > 0 ? true : false
);
useActivateExperiment('EXP_LINEAR_TV', activateLinearTVExp);
// EXP_LINEAR_TV

return { __sfc: true,AVODHeroSection, TVODHeroSection, TitleDetailStreamingChart, TitleSynopsis, ArticleBlock, UpcomingContent, EpisodesList, TitleCredits, SpinningText, TitleDetailSimilarTitlesV2, TitleDetailQuickActionBarV1, AdUnit, SnigelExpAdUnit, activeClipId, streamingChart, container, displayTextRecommendationModal, userStore, newBuyboxView, buyboxExpView, isAdmin, isEditor, loggedInNewBuyboxView, isLoggedIn, experimentsLoaded, country, language, urlMetadata, t, emits, props, title, titleName, titleId, titleObjectId, seasonName, seasonNumber, seasonTotalEpisodeCount, isShow, isSeason, recentEpisodes, recentEpisodesAvailable, isTitleUpcoming, showDetails, originalTitle, isInWatchList, isInternationalOffersActive, hasInternationalOffers, isInternationalOffersTitle, AVODOffer, upcomingReleases, isArbitrating, activateExpGridSwiper, catalogueItem, activateGridRedesignExps, gridMoreInfoActiveVariant, activateMBRExp, mergeBuyRentExpVariant, activateUnderperformingUrlsExp, isGridBuyboxDefault, activatePrimePlayPromoExp, isSnigelAdSpaceAllowed, snigelExpVariant, snigelExpInfo, sharedBuyboxProviderFilter, buyboxOffers, isListSponsoredAd, isShadowCampaign, hasActiveSRCampaign, streamingCharts, hasNonCinemaOffers, displayStreamingChart, hasJWTVRentalOffer, activeHeroSection, overrideDefaultBuyBoxType, overrideBuyBoxTypeActive, buyBoxTypeBindings, updateOverrideBuyBoxType, showSynopsis, editorialSynopsis, showSimilarTitles, aboutHeadline, showNewBuyBox, showOffersHistory, episodesTitle, maxEpisodes, episodes, showTextRecommendationTools, subgenres, isMounted, isExperimentsLoadedAndNonSnigel, displayThirdAd, titleClips, hasTrailers, getTitleClips, trackWtw, promotedBundles, hasPromotedOffers, openTextRecommendationModal, closeTextRecommendationModal, textRecommendationArray, visibleSectionState, startPageJwtvInfo, wasAcquiredToStartPageJwtv, activateProviderBuyboxExp, activateLinearTVExp, SimilarTitles, BuyBoxBanner, ListBuyBox, SectionContainer, TitleDetailJumpLinksV1, TitleDetailTitleClipsV1, isTitleSeason, isTitleShow, TitleDetailHeroV1, TitleDetailTitleSidebarV1, BuyBoxTypeSwitcher, GridBuyBox, PromotedGridBuyBox, TextRecommendationModalV2, TextRecommendations, SeasonListV1, StreamingOffersHistory }
}

})